import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import TopBar from '../components/TopBar';
import ConciergeLauncher from '../components/ConciergeLauncher';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowRight } from 'lucide-react';

const HomePage = () => {
  const { currentUser } = useAuth();
  const { pathways, resources } = useData();
  const navigate = useNavigate();

  // Role-aware content
  const getRoleContent = () => {
    switch (currentUser?.role) {
      case 'INVESTOR':
        return {
          eyebrow: 'Recommended for you · Investor',
          heading: 'Discover bankable opportunities',
          lede: 'Assess grid opportunities with live market intelligence, benchmark feasibility, and reach the developers and experts behind credible projects.',
          pathways: pathways.filter(p => p.audience === 'INVESTOR')
        };
      case 'GOVERNMENT':
        return {
          eyebrow: 'Recommended for you · Government & Regulators',
          heading: 'Shape the enabling environment',
          lede: 'Draw on policy and regulatory benchmarks, learn from peer governments, and stay close to how grid modernisation is evolving on the ground.',
          pathways: pathways.filter(p => p.audience === 'GOVERNMENT')
        };
      default: // PRACTITIONER
        return {
          eyebrow: 'Recommended for you · Practitioner',
          heading: 'Guided pathways',
          lede: 'Find the right knowledge, build real capability, and connect with the people turning it into deployments. An AI guide walks with you from question to action.',
          pathways: pathways.filter(p => p.audience === 'PRACTITIONER')
        };
    }
  };

  const roleContent = getRoleContent();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--bg))' }}>
      <TopBar />

      {/* Hero Section */}
      <section className="wrap section">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">Learn · Know · Connect</span>
            <h1 className="mt-3 mb-4 text-4xl md:text-5xl lg:text-6xl leading-tight">
              One intelligent home for <em style={{ fontStyle: 'italic', color: 'hsl(var(--primary))' }}>grid modernisation</em> — not another link library.
            </h1>
            <p className="lede mb-8">
              {roleContent.lede}
            </p>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" onClick={() => navigate('/ask')} style={{ backgroundColor: 'hsl(var(--primary))' }}>
                Start with a question <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate('/knowledge')}>
                Browse the repository
              </Button>
            </div>
            <div className="flex gap-8 mt-8">
              <div>
                <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>2,400+</div>
                <div className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>Curated resources</div>
              </div>
              <div>
                <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>38</div>
                <div className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>Countries active</div>
              </div>
              <div>
                <div className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>94</div>
                <div className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>CoE-trained experts</div>
              </div>
            </div>
          </div>

          <div>
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'hsl(var(--mint))' }} />
                <strong className="text-sm">Grid Guide</strong>
                <span className="ml-auto text-xs" style={{ color: 'hsl(var(--ink-faint))' }}>AI concierge</span>
              </div>
              <p className="text-lg font-medium mb-4" style={{ color: 'hsl(var(--ink-2))' }}>What are you trying to do today?</p>
              <div 
                className="px-4 py-3 rounded-lg border cursor-pointer"
                onClick={() => navigate('/ask')}
                style={{ borderColor: 'hsl(var(--line))', backgroundColor: 'hsl(var(--surface-2))' }}
              >
                <span style={{ color: 'hsl(var(--ink-muted))' }}>e.g. "Help me procure a 20MWh BESS system..."</span>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Pathways Section */}
      <section className="section wrap">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="eyebrow">{roleContent.eyebrow}</span>
            <h2 className="mt-3">{roleContent.heading}</h2>
            <p className="mt-2" style={{ color: 'hsl(var(--ink-muted))' }}>
              Goal-based journeys that weave Learn, Know and Connect together.
            </p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/knowledge')}>All pathways <ArrowRight className="ml-2 h-4 w-4" /></Button>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {roleContent.pathways.map((pathway) => (
            <Card
              key={pathway.id}
              className="p-6 cursor-pointer hover:shadow-lg relative overflow-hidden"
              onClick={() => navigate(`/pathways/${pathway.slug}`)}
            >
              <div className="absolute top-4 right-4">
                <ArrowRight className="h-5 w-5" style={{ color: 'hsl(var(--ink-muted))' }} />
              </div>
              <div className="flex items-center justify-center w-12 h-12 rounded-lg mb-4" style={{ backgroundColor: pathway.iconBg }}>
                <span className="text-2xl">{pathway.icon}</span>
              </div>
              <h3 className="mb-2">{pathway.title}</h3>
              <p className="text-sm mb-4" style={{ color: 'hsl(var(--ink-muted))' }}>{pathway.summary}</p>
              <div className="flex items-center gap-2 text-xs">
                {pathway.steps.slice(0, 3).map((step, idx) => (
                  <React.Fragment key={idx}>
                    <span className="px-2 py-1 rounded" style={{ backgroundColor: 'hsl(var(--surface-2))', color: 'hsl(var(--ink-2))' }}>
                      {step.pillar}
                    </span>
                    {idx < 2 && <span style={{ color: 'hsl(var(--ink-faint))' }}>→</span>}
                  </React.Fragment>
                ))}
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Three Pillars */}
      <section className="section wrap">
        <div className="grid md:grid-cols-3 gap-6">
          <Card className="p-6">
            <div className="text-xs font-semibold mb-2" style={{ color: 'hsl(var(--primary))' }}>Learn</div>
            <h3 className="mb-2">Academy</h3>
            <p className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>Structured courses, the training calendar, and expert talks — linked to the physical Centre of Excellence.</p>
          </Card>
          <Card className="p-6">
            <div className="text-xs font-semibold mb-2" style={{ color: 'hsl(var(--primary))' }}>Know</div>
            <h3 className="mb-2">Knowledge Repository</h3>
            <p className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>A searchable, AI-curated library of policy, procurement, market intelligence and project showcases.</p>
          </Card>
          <Card className="p-6">
            <div className="text-xs font-semibold mb-2" style={{ color: 'hsl(var(--primary))' }}>Connect</div>
            <h3 className="mb-2">Community</h3>
            <p className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>Profiles, topic spaces, expert matching and investor-facing opportunity discovery.</p>
          </Card>
        </div>
      </section>

      {/* Featured Resources */}
      <section className="section wrap">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2>Fresh in the repository</h2>
            <p className="mt-2" style={{ color: 'hsl(var(--ink-muted))' }}>AI-tagged and surfaced based on what's relevant to your work right now.</p>
          </div>
          <Button variant="ghost" onClick={() => navigate('/knowledge')}>Explore Knowledge <ArrowRight className="ml-2 h-4 w-4" /></Button>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {resources.slice(0, 3).map((resource) => (
            <Card key={resource.id} className="p-6 cursor-pointer hover:shadow-lg" onClick={() => navigate(`/knowledge/${resource.id}`)}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs px-2 py-1 rounded" style={{
                  backgroundColor: resource.type === 'TOOL' ? 'hsl(var(--accent-soft))' : 'hsl(var(--blue-soft))',
                  color: resource.type === 'TOOL' ? 'hsl(var(--accent-ink))' : 'hsl(var(--blue))'
                }}>
                  {resource.type === 'TOOL' ? 'Interactive tool' : resource.type === 'POLICY' ? 'Policy' : 'Resource'}
                </span>
                <span className="text-xl">{resource.type === 'TOOL' ? '⚡' : '📄'}</span>
              </div>
              <h4 className="mb-2">{resource.title}</h4>
              <p className="text-sm mb-4" style={{ color: 'hsl(var(--ink-muted))' }}>{resource.summary}</p>
              <div className="text-xs" style={{ color: 'hsl(var(--ink-faint))' }}>
                {resource.sourceOrg} · {new Date(resource.publishedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </div>
            </Card>
          ))}
        </div>
      </section>

      <ConciergeLauncher />

      {/* Footer */}
      <footer className="wrap py-8 border-t" style={{ borderColor: 'hsl(var(--line))' }}>
        <p className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>
          <strong>GridCommons</strong> — Knowledge-to-action platform for GEAPP's <strong>Grids of the Future</strong> program
        </p>
      </footer>
    </div>
  );
};

export default HomePage;
